"use client";
import { External, IHour, IResource, Office } from "@gettactic/api";
import { EmptyView } from "@gettactic/components/src/data-display/EmptyView/EmptyView";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { IoAdd as Plus } from "react-icons/io5";
import { CreateOrEditExternalIDs } from "@/features/integrations/components/external-ids/CreateOrEditExternalIDs";
import { IntegrationsExternalIdsTable } from "@/features/integrations/components/external-ids/IntegrationsExternalIdsTable";
import { useExternalsQuery } from "@/lib/hooks/useExternalsQuery";
import { SectionContentWrapper } from "@cmp-src/layout/SectionContentWrapper";

type Props = {
  office: Office;
  resources?: IResource[];
  title: string;
};

export type IHourWithResName = IHour & {
  resName?: string;
};

export function groupSharedKey(external: External) {
  return `${external.id}_${external.object_type}_${external.object_id}_${external.external_id}_${
    external.name
  }`;
}

export function IntegrationsExternalIdsList({
  title,
  office,
  resources
}: Props) {
  const queryClient = useQueryClient();
  const externals = useExternalsQuery(resources?.[0].id || "", "resources");
  const invalidateQuery = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ["useExternalsQuery"]
      }),
    [office]
  );

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  return (
    <>
      <CreateOrEditExternalIDs
        key={isCreateOpen ? "new" : "new-close"}
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        resources={resources}
        invalidateQuery={invalidateQuery}
      />
      <SectionContentWrapper
        title={title}
        isLoading={externals.isLoading}
        isEmpty={(externals?.data || []).length === 0}
        emptyView={
          <EmptyView
            title="No External IDs"
            message="Get started by clicking the 'Add External ID' button"
            actions={[
              {
                label: "Add External ID",
                onClick: () => setIsCreateOpen(true),
                icon: Plus
              }
            ]}
          />
        }
        actions={[
          {
            label: "Add External ID",
            onClick: () => setIsCreateOpen(true),
            icon: <Plus className="h-4 w-4" />
          }
        ]}
      >
        <IntegrationsExternalIdsTable
          data={externals.data || []}
          invalidateQuery={invalidateQuery}
        />
      </SectionContentWrapper>
    </>
  );
}
